import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { useAppConfig } from '../../../contexts/AppConfigContext';
import { useTenantData } from '../../../contexts/TenantDataContext';
import { useCompanyLocationMapCenter } from '@/hooks/useCompanyLocationMapCenter';
import type { Routemize_DTOs_CompanyLocationDto } from '../../../api/models/Routemize_DTOs_CompanyLocationDto';
import { addressManager, type UserAddress } from '../../../business/users/AddressManager';
import { resolveHomeUserAddress } from '../../../utils/userAddressUtils';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  addressFieldsFrom,
  emptyAddressFields,
  isAddressComplete,
  saveHomeAddress,
  type AddressFieldValues,
} from '../../../business/onboarding/onboardingLocations';
import { AddressFields } from '../shared/AddressFields';
import { AddressPinMap } from '../shared/AddressPinMap';
import { Callout, StepIntro } from '../shared/StepShell';

/** Geocoding runs off the committed value, not every keystroke. */
const GEOCODE_DEBOUNCE_MS = 600;

export const HomeAddressStep: React.FC = () => {
  const { completeStep } = useOnboarding();
  const { config } = useAppConfig();
  const { country: tenantCountry } = useTenantData();
  const userId = config?.currentUser?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [homeAddress, setHomeAddress] = useState<UserAddress | null>(null);
  const [address, setAddress] = useState<AddressFieldValues>(() =>
    emptyAddressFields(tenantCountry)
  );
  const [settled, setSettled] = useState<AddressFieldValues | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const addresses = userId ? await addressManager.getUserAddresses(userId) : [];
      if (cancelled) return;

      const home = resolveHomeUserAddress(addresses ?? []) ?? null;
      setHomeAddress(home);
      if (home) {
        const fields = addressFieldsFrom(home, tenantCountry);
        setAddress(fields);
        setSettled(fields);
      }
      setIsLoading(false);
    };

    load().catch(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, tenantCountry]);

  const complete = isAddressComplete(address);
  useStepValidity(complete);

  useEffect(() => {
    if (!complete) {
      setSettled(null);
      return;
    }
    const timer = setTimeout(() => setSettled(address), GEOCODE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [complete, address]);

  const mapLocation = useMemo<Routemize_DTOs_CompanyLocationDto | null>(
    () =>
      settled
        ? {
            addressLine1: settled.addressLine1,
            city: settled.city,
            state: settled.state,
            zipCode: settled.zipCode,
            country: settled.country,
          }
        : null,
    [settled]
  );

  const { center, isLoading: isCenterLoading } = useCompanyLocationMapCenter(
    mapLocation,
    !isLoading
  );

  useStepSaver(
    ONBOARDING_STEP.HOME,
    useCallback(async () => {
      if (!userId) throw new Error('Your account is still loading. Please try again.');
      setHomeAddress(await saveHomeAddress(userId, address, homeAddress));
      completeStep(ONBOARDING_STEP.HOME);
    }, [userId, address, homeAddress, completeStep])
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StepIntro>
        Every route and drive time is measured from here, so it's worth getting exact. Start
        typing and pick your address from the suggestions.
      </StepIntro>

      <div className="rounded-xl border border-ink-200 bg-surface p-4 sm:p-5">
        <AddressFields
          value={address}
          onChange={setAddress}
          placeholder="Start typing your home address…"
        />
      </div>

      <div className="rounded-xl border border-ink-200 bg-surface p-4">
        <div className="mb-2.5 flex items-center gap-2">
          {center ? (
            <>
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success-600" />
              <p className="text-sm font-medium text-ink-800">
                Found it — check the pin looks right
              </p>
            </>
          ) : (
            <p className="text-sm font-medium text-ink-800">Location preview</p>
          )}
        </div>
        <AddressPinMap
          center={center}
          isLoading={isCenterLoading}
          emptyMessage="Fill in the address above and we'll show you where it lands."
        />
      </div>

      <Callout tone="info" icon={Lock}>
        Only you and your team see this. Customers never see your home address — it's used to
        work out drive times and which jobs are close to you.
      </Callout>
    </div>
  );
};
